package top.wcpe.allincore.dsl.ir.model

/**
 * canvas 元素的 IR 数据载荷。
 *
 * 字段：
 *  - `width` / `height` —— canvas 像素尺寸常量（u16）；当
 *      `widthExpr`/`heightExpr` 非 null 时此字段值无意义，
 *      仅用于占位（落到 codec 上仍按 u16 写出）。
 *  - `drawBlob` —— 编译后的 draw 指令字节码，见 DrawBlob。
 *  - `durationMs` —— 单周期时长（毫秒），M11.2 引入；默认 1000。
 *  - `loopMode` —— 与 `UiAnimation.loopMode` 同语义：
 *      `null`/空串 = 单次播放，`"true"` = 循环，`"mirror"` = 镜像往返。
 *  - `widthExpr` / `heightExpr` / `durationExpr` ——
 *      M11.7 Stage 1 引入；表达式字节码（与 binding EXPR 字节流复用 ExprBlob 格式），
 *      null 表示该字段是常量。运行时若非 null 则每帧求值，覆盖常量字段值。
 *
 * 不可变：构造期对 `drawBlob` / 三个表达式 blob 做防御式拷贝；getter 同样返回拷贝。
 */
class UiCanvasData(
    width: Int,
    height: Int,
    drawBlob: ByteArray,
    durationMs: Double,
    loopMode: String?,
    widthExpr: ByteArray?,
    heightExpr: ByteArray?,
    durationExpr: ByteArray?,
) {

    // val 属性生成 getWidth()/getHeight()/getDurationMs()/getLoopMode()，与原 Java getter 名一致。
    val width: Int
    val height: Int
    private val drawBlobInternal: ByteArray
    val durationMs: Double
    val loopMode: String?

    // M11.7 Stage 1：可空表达式 blob；null 表示常量路径。
    private val widthExprInternal: ByteArray?
    private val heightExprInternal: ByteArray?
    private val durationExprInternal: ByteArray?

    init {
        require(!(width < 0 || width > 0xFFFF)) { "canvas width 超出 u16 范围: " + width }
        require(!(height < 0 || height > 0xFFFF)) { "canvas height 超出 u16 范围: " + height }
        require(!(durationMs.isNaN() || durationMs < 0.0)) { "canvas durationMs 必须为非负有限值: " + durationMs }
        requireNotNull(drawBlob) { "drawBlob" }
        this.width = width
        this.height = height
        // 防御式拷贝
        this.drawBlobInternal = drawBlob.copyOf()
        this.durationMs = durationMs
        // 空串规范化为 null，避免运行时分支重复判空
        this.loopMode = if (loopMode == null || loopMode.isEmpty()) null else loopMode
        this.widthExprInternal = widthExpr?.copyOf()
        this.heightExprInternal = heightExpr?.copyOf()
        this.durationExprInternal = durationExpr?.copyOf()
    }

    /** M11.6/M11.2 兼容构造器：三个表达式 blob 均为 null（纯常量路径）。 */
    constructor(width: Int, height: Int, drawBlob: ByteArray, durationMs: Double, loopMode: String?) :
        this(width, height, drawBlob, durationMs, loopMode, null, null, null)

    /** 旧 M11.1 构造器：默认 duration=1000ms，loopMode="true"（循环）。 */
    constructor(width: Int, height: Int, drawBlob: ByteArray) :
        this(width, height, drawBlob, DEFAULT_DURATION_MS, DEFAULT_LOOP_MODE, null, null, null)

    /** 返回 drawBlob 字节数组的拷贝，外部修改不会影响内部状态。 */
    // 属性 + 自定义 getter：Java 侧仍见 getDrawBlob() 返回拷贝，Kotlin 侧以 .drawBlob 访问（同原 Java getter 语义）。
    val drawBlob: ByteArray
        get() = drawBlobInternal.copyOf()

    /** 宽度表达式 blob；null 表示走常量路径。返回拷贝。 */
    val widthExpr: ByteArray?
        get() = widthExprInternal?.copyOf()

    /** 高度表达式 blob；null 表示走常量路径。返回拷贝。 */
    val heightExpr: ByteArray?
        get() = heightExprInternal?.copyOf()

    /** duration 表达式 blob；null 表示走常量路径。返回拷贝。 */
    val durationExpr: ByteArray?
        get() = durationExprInternal?.copyOf()

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiCanvasData) return false
        return width == other.width &&
            height == other.height &&
            durationMs.compareTo(other.durationMs) == 0 &&
            loopMode == other.loopMode &&
            drawBlobInternal.contentEquals(other.drawBlobInternal) &&
            widthExprInternal.contentEquals(other.widthExprInternal) &&
            heightExprInternal.contentEquals(other.heightExprInternal) &&
            durationExprInternal.contentEquals(other.durationExprInternal)
    }

    // 逐位复刻原 Java：种子 Objects.hash(width, height, durationMs, loopMode)（种子 1、31 倍累乘、
    // 各元素用其 boxed hashCode），再逐个 31*h + Arrays.hashCode(各 blob)。
    override fun hashCode(): Int {
        var h = 1
        h = 31 * h + width.hashCode()
        h = 31 * h + height.hashCode()
        h = 31 * h + durationMs.hashCode()
        h = 31 * h + (loopMode?.hashCode() ?: 0)
        h = 31 * h + drawBlobInternal.contentHashCode()
        h = 31 * h + widthExprInternal.contentHashCode()
        h = 31 * h + heightExprInternal.contentHashCode()
        h = 31 * h + durationExprInternal.contentHashCode()
        return h
    }

    override fun toString(): String =
        "UiCanvasData{" + width + "x" + height +
            ", drawBlob=" + drawBlobInternal.size + "B" +
            ", durationMs=" + durationMs +
            ", loopMode=" + loopMode +
            ", widthExpr=" + (if (widthExprInternal == null) "null" else widthExprInternal.size.toString() + "B") +
            ", heightExpr=" + (if (heightExprInternal == null) "null" else heightExprInternal.size.toString() + "B") +
            ", durationExpr=" + (if (durationExprInternal == null) "null" else durationExprInternal.size.toString() + "B") +
            "}"

    companion object {
        /** 默认单周期时长（毫秒）。 */
        const val DEFAULT_DURATION_MS: Double = 1000.0

        /** 默认 loopMode："true" 表示循环（与 M11.1 兼容默认）。 */
        const val DEFAULT_LOOP_MODE: String = "true"
    }
}
